export interface SSEEvent {
  type: string;
  data: Record<string, any>;
}

/**
 * Incrementally parses Server-Sent Events from a raw text stream.
 *
 * Handles chunk-boundary splitting: if a chunk ends mid-event the
 * incomplete data stays in an internal buffer and is prepended to
 * the next chunk fed in.
 *
 * Usage:
 *   const parser = new SSEParser();
 *   // inside read loop:
 *   const events = parser.feed(chunk);
 *   for (const event of events) { ... }
 */
export class SSEParser {
  private buffer = '';

  /**
   * Feed a raw UTF-8 chunk from the ReadableStream.
   * Returns zero or more fully parsed SSE events.
   */
  feed(chunk: string): SSEEvent[] {
    this.buffer += chunk;
    const events: SSEEvent[] = [];

    // SSE events are delimited by a blank line (\n\n).
    let idx: number;
    while ((idx = this.buffer.indexOf('\n\n')) !== -1) {
      const raw = this.buffer.slice(0, idx);
      this.buffer = this.buffer.slice(idx + 2);

      if (!raw.trim()) continue;

      let eventType = 'message'; // SSE default
      let dataLine = '';

      for (const line of raw.split('\n')) {
        if (line.startsWith('event: ')) {
          eventType = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          // Per SSE spec, multiple data: lines are joined with \n
          dataLine += (dataLine ? '\n' : '') + line.slice(6);
        } else if (line.startsWith('data:')) {
          dataLine += (dataLine ? '\n' : '') + line.slice(5);
        }
      }

      if (!dataLine) continue;

      try {
        events.push({ type: eventType, data: JSON.parse(dataLine) });
      } catch {
        console.warn('[SSEParser] Failed to parse data line:', dataLine);
      }
    }

    return events;
  }

  /** Discard any buffered partial data. */
  reset(): void {
    this.buffer = '';
  }
}
